import React, { useState } from "react";
import { Table, Button, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const EmployeeExcelUploadModel = ({
  profile,
  setUploadModalVisible,
  fetchEmployees,
  uploadLoading,
  setUploadLoading,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState([]);
  const [error, setError] = useState(null);

  const handleExcelUpload = async () => {
    if (fileList.length === 0) {
      setError(t("messages.error.selectExcelFile"));
      return;
    }

    // Clear previous errors
    setError(null);

    const formData = new FormData();
    formData.append("excelFile", fileList[0].originFileObj || fileList[0]);
    formData.append("companyId", profile.companyId);

    setUploadLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/Employee/import-excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const { data, summary } = response.data;
      
      // Handle different scenarios
      if (summary.successCount > 0 && summary.failureCount > 0) {
        // Partial success - some added, some failed
        toast.success(
          t("messages.success.employeesUploaded", { count: summary.successCount })
        );
        toast.warning(
          t("messages.warning.employeesFailed", { count: summary.failureCount })
        );
        setError(`${t("upload.partialResults")}:\n✅ ${summary.successCount} ${t("upload.employeesAdded")}\n❌ ${summary.failureCount} ${t("upload.employeesFailed")}\n\n${t("upload.errorDetails")}:\n${data.errors.join('\n')}`);
        
        // Keep modal open to show detailed results
      } else if (summary.successCount > 0 && summary.failureCount === 0) {
        // Complete success
        toast.success(
          t("messages.success.allEmployeesUploaded", { count: summary.successCount })
        );
        setUploadModalVisible(false);
        setFileList([]);
        setError(null);
      } else if (summary.successCount === 0 && summary.failureCount > 0) {
        // Complete failure
        setError(`${t("upload.uploadFailed")}:\n\n${data.errors.join('\n')}`);
      }
      
      fetchEmployees(); // Refresh the employee list
    } catch (error) {
      console.error("Error uploading Excel:", error);
      setError(error.response?.data?.message || "Error uploading Excel file");
    } finally {
      setUploadLoading(false);
    }
  };

  const validateExcelFile = (file) => {
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError(t("messages.error.onlyExcelFiles"));
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError(t("messages.error.fileSizeLimit"));
      return false;
    }

    // Clear error if file is valid
    setError(null);
    return true;
  };

  const uploadProps = {
    beforeUpload: (file) => {
      if (validateExcelFile(file)) {
        setFileList([file]);
      }
      return false; // Prevent automatic upload
    },
    fileList,
    onRemove: () => setFileList([]),
    maxCount: 1,
    accept: ".xlsx,.xls",
  };

  const excelFormatData = [
    {
      key: 1,
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      employeeId: "E123",
      phoneNumber: "987-654-3210",
      role: "Software Engineer",
    },
    {
      key: 2,
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      employeeId: "E124",
      phoneNumber: "123-456-7890",
      role: "Project Manager",
    },
  ];

  const formatColumns = [
    { title: t("form.firstName"), dataIndex: "firstName", key: "firstName" },
    { title: t("form.lastName"), dataIndex: "lastName", key: "lastName" },
    { title: t("form.email"), dataIndex: "email", key: "email" },
    { title: t("form.employeeId"), dataIndex: "employeeId", key: "employeeId" },
    { title: t("form.phone"), dataIndex: "phoneNumber", key: "phoneNumber" },
    { title: t("form.role"), dataIndex: "role", key: "role" },
  ];

  return (
    <div className="space-y-4">
      {/* Excel Format Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <h4 className="font-medium text-yellow-800 mb-2">
          {t("upload.requiredExcelFormat")}:
        </h4>

        <div className="overflow-x-auto mb-2">
          <Table
            columns={formatColumns}
            dataSource={excelFormatData}
            pagination={false}
            size="small"
            className="border rounded-lg"
          />
        </div>

        {/* <div className="text-xs text-yellow-700 space-y-1"> */}
        {/* <p>
            <strong>Required:</strong> First Name, Last Name, Email (must be
            unique)
          </p>
          <p>
            <strong>Optional:</strong> Role, Employee ID
          </p>
        </div> */}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <ul className="text-sm text-blue-700 space-y-1">
          <li>
            • {t("upload.excelFileFormat")}
          </li>
          <li>• {t("upload.firstRowHeaders")}</li>
          <li>• {t("upload.emptyRowsSkipped")}</li>
        </ul>
      </div>

      <Upload {...uploadProps} className="top-10">
        <Button icon={<UploadOutlined />} size="large" className="w-full">
          {t("buttons.selectExcelFile")}
        </Button>
      </Upload>

      {/* Error Message Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start">
            <div className="text-red-800 w-full">
              <div className="flex items-center mb-2">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium text-sm">{t("upload.uploadResults")}:</span>
              </div>
              <div className="text-sm whitespace-pre-line bg-white p-3 rounded border max-h-40 overflow-y-auto">
                {error}
              </div>
              {/* Add close button for error results */}
              <div className="mt-3 flex justify-end">
                <Button
                  size="small"
                  onClick={() => {
                    setError(null);
                    setFileList([]);
                    setUploadModalVisible(false);
                  }}
                >
                  {t("buttons.close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {fileList.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-green-700 text-sm">
            ✓ {t("upload.selectedFile")}: {fileList[0].name}
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              type="primary"
              loading={uploadLoading}
              onClick={handleExcelUpload}
              className="flex-1"
            >
              {t("buttons.uploadExcelFile")}
            </Button>
            <Button
              onClick={() => {
                setFileList([]);
                setError(null);
                setUploadModalVisible(false);
              }}
            >
              {t("buttons.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeExcelUploadModel;
